import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getPendingBlogPosts, approveBlogPost, rejectBlogPost, deleteBlogPost, getBlogStatistics } from '../../services/firebase/blogService';
import './AdminContentModeration.css';

const AdminContentModeration = () => {
  const { user, isAdmin } = useAuth();
  
  const [pendingPosts, setPendingPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionInProgress, setActionInProgress] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    loadData();
  }, [isAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [postsResult, statsResult] = await Promise.all([
        getPendingBlogPosts(),
        getBlogStatistics(),
      ]);
      
      if (postsResult.success) {
        setPendingPosts(postsResult.data);
      }
      if (statsResult.success) {
        setStats(statsResult.data);
      }
    } catch (err) {
      setError('Error loading data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (postId) => {
    try {
      setActionInProgress(postId);
      const result = await approveBlogPost(postId, reviewNotes);
      
      if (result.success) {
        setPendingPosts(pendingPosts.filter(p => p.id !== postId));
        setSelectedPost(null);
        setReviewNotes('');
        if (stats) {
          setStats(prev => ({
            ...prev,
            pending: prev.pending - 1,
            approved: prev.approved + 1,
          }));
        }
      } else {
        setError(result.error || 'Failed to approve post');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = async (postId) => {
    if (!rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      setActionInProgress(postId);
      const result = await rejectBlogPost(postId, rejectionReason);
      
      if (result.success) {
        setPendingPosts(pendingPosts.filter(p => p.id !== postId));
        setSelectedPost(null);
        setRejectionReason('');
        if (stats) {
          setStats(prev => ({
            ...prev,
            pending: prev.pending - 1,
            rejected: prev.rejected + 1,
          }));
        }
      } else {
        setError(result.error || 'Failed to reject post');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      setActionInProgress(postId);
      const result = await deleteBlogPost(postId);
      
      if (result.success) {
        setPendingPosts(pendingPosts.filter(p => p.id !== postId));
        setSelectedPost(null);
      } else {
        setError(result.error || 'Failed to delete post');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="moderation-container">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only administrators can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="moderation-container">
      <div className="moderation-header">
        <h1>🛡️ Content Moderation</h1>
        <p>Review and manage blog posts from vendors</p>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>{stats.totalPosts}</h3>
              <p>Total Posts</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{stats.approved}</h3>
              <p>Approved</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <h3>{stats.pending}</h3>
              <p>Pending Review</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">❌</div>
            <div className="stat-content">
              <h3>{stats.rejected}</h3>
              <p>Rejected</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">👁️</div>
            <div className="stat-content">
              <h3>{stats.totalViews}</h3>
              <p>Total Views</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          ❌ {error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="moderation-content">
        {/* Pending Posts List */}
        <div className="posts-list">
          <h2>📋 Posts Pending Review ({pendingPosts.length})</h2>
          
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading pending posts...</p>
            </div>
          ) : pendingPosts.length === 0 ? (
            <div className="empty-state">
              <p className="empty-icon">✅</p>
              <h3>All caught up!</h3>
              <p>No posts pending review.</p>
            </div>
          ) : (
            <div className="posts-queue">
              {pendingPosts.map(post => (
                <div
                  key={post.id}
                  className={`post-item ${selectedPost?.id === post.id ? 'selected' : ''}`}
                  onClick={() => setSelectedPost(post)}
                >
                  <div className="post-item-header">
                    <h4>{post.title}</h4>
                    <span className="post-vendor">by {post.vendorName}</span>
                  </div>
                  <p className="post-item-excerpt">{post.excerpt}</p>
                  <div className="post-item-meta">
                    <span>📅 {new Date(post.createdAt?.toDate?.() || post.createdAt).toLocaleDateString()}</span>
                    <span>📝 {post.content.length} chars</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Post Details Panel */}
        {selectedPost && (
          <div className="post-details">
            <div className="details-header">
              <h2>{selectedPost.title}</h2>
              <button
                className="btn-close"
                onClick={() => setSelectedPost(null)}
              >
                ✕
              </button>
            </div>

            {selectedPost.featuredImage && (
              <div className="post-featured-image">
                <img src={selectedPost.featuredImage} alt={selectedPost.title} />
              </div>
            )}

            <div className="post-meta-info">
              <div className="meta-item">
                <strong>Author:</strong> {selectedPost.vendorName}
              </div>
              <div className="meta-item">
                <strong>Category:</strong> {selectedPost.category}
              </div>
              <div className="meta-item">
                <strong>Submitted:</strong> {new Date(selectedPost.createdAt?.toDate?.() || selectedPost.createdAt).toLocaleString()}
              </div>
              {selectedPost.tags?.length > 0 && (
                <div className="meta-item">
                  <strong>Tags:</strong>
                  <div className="tags-display">
                    {selectedPost.tags.map(tag => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="post-excerpt-display">
              <h4>Excerpt</h4>
              <p>{selectedPost.excerpt}</p>
            </div>

            <div className="post-content-display">
              <h4>Content Preview</h4>
              <div className="content-preview">
                {selectedPost.content.substring(0, 500)}
                {selectedPost.content.length > 500 ? '...' : ''}
              </div>
              <p className="content-length">{selectedPost.content.length} characters total</p>
            </div>

            {/* Moderation Actions */}
            <div className="moderation-actions">
              <div className="action-group">
                <h4>✅ Approve Post</h4>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Optional review notes for vendor..."
                  rows="3"
                />
                <button
                  className="btn-approve"
                  onClick={() => handleApprove(selectedPost.id)}
                  disabled={actionInProgress === selectedPost.id}
                >
                  {actionInProgress === selectedPost.id ? 'Processing...' : 'Approve & Publish'}
                </button>
              </div>

              <div className="action-group">
                <h4>❌ Reject Post</h4>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this post is being rejected..."
                  rows="3"
                  required
                />
                <button
                  className="btn-reject"
                  onClick={() => handleReject(selectedPost.id)}
                  disabled={!rejectionReason.trim() || actionInProgress === selectedPost.id}
                >
                  {actionInProgress === selectedPost.id ? 'Processing...' : 'Reject & Notify Vendor'}
                </button>
              </div>

              <div className="action-group">
                <h4>🗑️ Delete Post</h4>
                <p className="warning">This action cannot be undone.</p>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(selectedPost.id)}
                  disabled={actionInProgress === selectedPost.id}
                >
                  {actionInProgress === selectedPost.id ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContentModeration;
